import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { change, Field, FieldArray, formValueSelector } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import { format as pattern, length, numericality, required } from 'redux-form-validators';

import {
  CheckboxField,
  DateField,
  DateRangeField,
  ReduxSelectField,
  ReduxTextField,
  TextAreaRawHtmlField,
  TinyMCEField,
} from 'common/components';

import ArrayField from '../ArrayField';
import MultiArrayField from '../MultiArrayField';
import GroupArrayField from '../GroupArrayField';
import MasterGroupArrayField from '../MasterGroupArrayField';
import GenerateArrayField from '../GenerateArrayField';
import SymbolGenerateArrayField from '../SymbolGenerateArrayField';
import ObjectField from '../ObjectField';
import DynamicField from '../DynamicField';
import TagAutomationField from '../TagAutomationField';

import { staticStyles } from './style';
import SymbolArrayField from '../SymbolArrayField';
import MasterSymbolArrayField from '../MasterSymbolArrayField';
import AccountGroupArrayField from '../AccountGroupArrayField';
import AccountSymbolArrayField from '../AccountSymbolArrayField';
import MasterGenerateArrayField from '../MasterGenerateArrayField';
import MasterSymbolGenerateArrayField from '../MasterSymbolGenerateArrayField';

// 条件禁用字段的包装组件
const ConditionalInputField = ({
  name,
  widget,
  title,
  props: fieldProps,
  submitting,
  withoutHeaderDescription,
  // isAllFieldsRequired,
  formName,
  validators,
  component,
  type,
}) => {
  const dispatch = useDispatch();
  // 使用 useMemo 稳定 formSelector，避免每次渲染都创建新的 selector
  const formSelector = useMemo(() => (formName ? formValueSelector(formName) : null), [formName]);

  // 动态验证：当 program 为 trade_volume 或 Traded Volume 时，base_for_volume 为必填
  // 直接获取 type 字段以避免对象引用导致的无限循环
  const programValueRaw = useSelector(state => {
    try {
      if (!formSelector || !state) return null;

      // 首先尝试直接获取 type 字段
      const typeValue = formSelector(state, 'basic.program.type');
      if (typeValue) {
        return typeValue;
      }

      // 如果 type 字段不存在，尝试从对象中提取
      const value = formSelector(state, 'basic.program');
      if (value && typeof value === 'object' && value.type) {
        return value.type;
      }

      // 如果是字符串，直接返回
      return value;
    } catch (error) {
      return null;
    }
  });

  // 使用 useMemo 缓存 programValue，确保字符串值稳定
  const programValue = useMemo(() => {
    // 确保返回的是字符串或 null，而不是对象
    if (programValueRaw && typeof programValueRaw === 'object' && programValueRaw.type) {
      return programValueRaw.type;
    }
    return programValueRaw;
  }, [programValueRaw]);

  // 检查是否是 base_for_volume 字段且 program 为 trade_volume 相关值
  const isBaseForVolume = name && name.endsWith('.base_for_volume');
  const isTradeVolume = programValue === 'traded_volume';
  const shouldRequireBaseForVolume = isBaseForVolume && isTradeVolume;

  // 条件禁用逻辑：检查 switch 字段的值
  // 支持的字段映射：value字段名 -> switch字段名
  const conditionalDisableMap = {
    margin_restriction_value: 'margin_restriction_switch',
    profit_ratio_cap_value: 'profit_ratio_cap_switch',
    min_closed_lot_restriction_deposit: 'min_closed_lot_restriction_switch',
    min_closed_lot_restriction_lot: 'min_closed_lot_restriction_switch',
    min_closed_lot_restriction_is_user: 'min_closed_lot_restriction_switch',
    carry_forward_value: 'carry_forward_switch',
    historical_profits_value: 'historical_profits_switch',
  };

  // 检查当前字段是否需要条件禁用
  const fieldName = name ? name.split('.').pop() : ''; // 获取字段名（最后一部分）
  const switchFieldName = conditionalDisableMap[fieldName];

  // 构建 switch 字段的完整路径（如果存在）
  const parentPath = switchFieldName && name ? name.substring(0, name.lastIndexOf('.')) : '';
  const switchFieldPath = switchFieldName && parentPath ? `${parentPath}.${switchFieldName}` : null;

  // 获取 switch 字段的值（保留原始值，不转换为布尔值）
  const switchValue = useSelector(state => {
    try {
      if (!formSelector || !state || !switchFieldPath) return undefined;
      return formSelector(state, switchFieldPath);
    } catch (error) {
      return undefined;
    }
  });

  // 获取当前字段的值
  const currentFieldValue = useSelector(state => {
    try {
      if (!formSelector || !state || !name) return undefined;
      return formSelector(state, name);
    } catch (error) {
      return undefined;
    }
  });

  // 使用 ref 跟踪是否已经初始化过（即 switch 是否曾经有过 true/false 值）
  const hasInitializedRef = useRef(false);

  // 提取 tipsText，只在 checkbox 字段中使用（提前解构以便后续使用）
  const { tips_text: tipsTextFromProps, tipsText, ...restProps } = fieldProps;

  // 当 switch 为 undefined 或 null 时，将对应的 value 字段设置为 0
  useEffect(() => {
    if (switchFieldName && widget !== 'checkbox' && formName && switchFieldPath) {
      // 检查 switch 是否为 undefined 或 null（未初始化状态）
      const isUninitialized = switchValue === undefined || switchValue === null;

      // 如果 switch 已经初始化过（曾经是 true 或 false），就不再自动设置值
      if (switchValue === true || switchValue === false) {
        hasInitializedRef.current = true;
        return; // 已经初始化，不需要再执行
      }

      // 只在未初始化时设置值为 0，且只设置一次
      if (isUninitialized && !hasInitializedRef.current) {
        // 检查当前值是否需要更新为 0
        const currentValue = currentFieldValue;
        const isZero = currentValue === 0 || currentValue === '0' || currentValue === 0.0;
        const isEmpty = currentValue === null || currentValue === undefined || currentValue === '';

        // 只有当值不是 0 或空时才更新
        if (!isZero && !isEmpty) {
          dispatch(change(formName, name, 0));
          hasInitializedRef.current = true; // 标记为已初始化
        } else if (isEmpty) {
          // 如果值为空，也设置为 0
          dispatch(change(formName, name, 0));
          hasInitializedRef.current = true; // 标记为已初始化
        }
      }
    }
  }, [switchValue, switchFieldName, widget, formName, name, dispatch, switchFieldPath]); // 移除 currentFieldValue 从依赖数组

  let isConditionallyDisabled = false;
  if (switchFieldName && formSelector) {
    // checkbox 类型字段（如 min_closed_lot_restriction_is_user）也支持条件禁用
    // 非 checkbox 字段保留原有的特殊处理逻辑
    if (widget !== 'checkbox') {
      // 特殊处理：如果是 carry_forward_value 或 historical_profits_value 字段且在编辑模式下（readonly 为 true），
      // 则始终禁用，不受 switch 控制
      const isCarryForwardValue = fieldName === 'carry_forward_value';
      const isHistoricalProfitsValue = fieldName === 'historical_profits_value';
      const isReadonly = restProps.readonly === true;

      if ((isCarryForwardValue || isHistoricalProfitsValue) && isReadonly) {
        // 编辑模式下，carry_forward_value 和 historical_profits_value 始终禁用
        isConditionallyDisabled = true;
      } else {
        // 其他情况：如果 switch 不是 true（即 false、undefined、null），则禁用该字段
        isConditionallyDisabled = switchValue !== true;
      }
    } else {
      // checkbox 字段：如果 switch 不是 true，则禁用该字段
      isConditionallyDisabled = switchValue !== true;
    }
  }

  // 添加 base_for_volume 的必填验证
  const finalValidators = [...validators];
  if (widget !== 'checkbox' && shouldRequireBaseForVolume) {
    finalValidators.push(required());
  }

  // TODO: ПЕРЕДЕЛАТЬ ЭТО ВСЕ!!!
  // TODO: Временное решение для tamam
  const placeholder =
    (title === 'justPhone' || title === 'Phone') &&
    (window.location.href === 'https://my.tamambrokerage.com/deposit' ||
      window.location.href === 'https://my.tamambrokerage.com/withdraw')
      ? '+256XXXXXXXXX'
      : '';

  // 构建 Field 组件的 props
  const fieldComponentProps = {
    name,
    textId: title,
    placeholder,
    type,
    component,
    validate: finalValidators,
    disabled: submitting || restProps.disabled || isConditionallyDisabled,
    customDefaultValue: restProps.default,
    tooltipId: withoutHeaderDescription ? restProps.description : '',
    tooltipTextId: withoutHeaderDescription ? restProps.description : '',
  };

  // 只在 checkbox 字段中添加 tipsText
  if (widget === 'checkbox' && (tipsTextFromProps || tipsText)) {
    fieldComponentProps.tipsText = tipsTextFromProps || tipsText;
  }

  // 添加 helpText 作为 description，显示在输入框下方
  // helpText 只在没有 tooltip 时显示（即 description 不作为 tooltip 时）
  if (restProps.helpText && (!restProps.description || !withoutHeaderDescription)) {
    fieldComponentProps.description = restProps.helpText;
  }

  // 针对数字和整数类型，添加 parse 逻辑，确保提交的是数字而不是字符串
  if (widget === 'number' || widget === 'integer') {
    fieldComponentProps.parse = value => {
      if (value === '' || value === null || value === undefined) {
        return null;
      }
      const str = String(value);
      const num = Number(str);
      // 非法数字（如单独的 "-"）保留原始字符串，避免变成 NaN 阻断输入
      if (Number.isNaN(num)) {
        return str;
      }
      // 用户输入中间态（如 "0."、".5"、"1.50" 等）与数字归一化结果不一致时，
      // 保留原始字符串，否则 Number() 会立即抹掉末尾小数点导致无法继续输入
      if (str !== String(num)) {
        return str;
      }
      return num;
    };
  }

  return (
    <div className="FormSchemaComposer__inner">
      {!withoutHeaderDescription && restProps.description && (
        <FormattedMessage id={restProps.description}>{txt => <p>{txt}</p>}</FormattedMessage>
      )}
      <Field {...fieldComponentProps} />
      <style jsx>{staticStyles}</style>
    </div>
  );
};

ConditionalInputField.propTypes = {
  name: PropTypes.string.isRequired,
  widget: PropTypes.string,
  title: PropTypes.string,
  props: PropTypes.object,
  submitting: PropTypes.bool,
  withoutHeaderDescription: PropTypes.bool,
  isAllFieldsRequired: PropTypes.bool,
  formName: PropTypes.string,
  validators: PropTypes.array,
  component: PropTypes.any,
  type: PropTypes.string,
};

ConditionalInputField.defaultProps = {
  widget: 'text',
  title: '',
  props: {},
  submitting: false,
  withoutHeaderDescription: false,
  isAllFieldsRequired: true,
  formName: '',
  validators: [],
  component: null,
  type: 'text',
};

export const inputComposer = (
  name,
  { widget = 'text', title, ...props },
  submitting,
  withoutHeaderDescription,
  isAllFieldsRequired = true,
  formName
) => {
  const component = widget === 'checkbox' ? CheckboxField : ReduxTextField;
  const validators = [];
  const type = widget === 'integer' ? 'number' : widget;

  // 检查是否是 base_for_volume 字段
  const isBaseForVolume = name && name.endsWith('.base_for_volume');

  if (widget !== 'checkbox') {
    // base_for_volume 的必填验证将在 ConditionalInputField 中处理
    if (!isBaseForVolume && ((isAllFieldsRequired && props.required !== false) || props.required === true)) {
      validators.push(required());
    }
  }

  const numericParams = {};
  if (widget === 'integer') {
    numericParams.int = true;
  }
  if (props.minimum) {
    numericParams.greaterThanOrEqualTo = props.minimum;
  }
  if (props.maximum) {
    numericParams.lessThanOrEqualTo = props.maximum;
  }

  if (Object.keys(numericParams).length) {
    validators.push(numericality(numericParams));
  }

  const stringParams = {};
  if (props.minLength) {
    stringParams.min = props.minLength;
  }
  if (props.maxLength) {
    stringParams.max = props.maxLength;
  }
  if (Object.keys(stringParams).length) {
    validators.push(length(stringParams));
  }

  if (props.validators) {
    props.validators.forEach(validator => {
      validators.push(
        pattern({
          with: new RegExp(validator.pattern),
          message: validator.error,
        })
      );
    });
  }

  // 使用条件禁用包装组件
  return (
    <ConditionalInputField
      name={name}
      widget={widget}
      title={title}
      props={props}
      submitting={submitting}
      withoutHeaderDescription={withoutHeaderDescription}
      isAllFieldsRequired={isAllFieldsRequired}
      formName={formName}
      validators={validators}
      component={component}
      type={type}
    />
  );
};

inputComposer.propTypes = {
  minimum: PropTypes.number,
  maximum: PropTypes.number,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  validators: PropTypes.array,
  description: PropTypes.string,
  default: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
};

inputComposer.defaultProps = {
  minimum: null,
  maximum: null,
  minLength: null,
  maxLength: null,
  validators: [],
  description: '',
  default: '',
};

export const requiredSelectField = value => {
  const errors = [];

  const isEmptyObject = [null, undefined].includes(value) || (typeof value === 'object' && !Object.keys(value).length);

  if (isEmptyObject) {
    errors.push('form.errors.presence');
  }

  return errors.length ? errors.map((error, index) => <FormattedMessage id={error} key={index} />) : null;
};

// 创建一个验证器函数来检查数组是否为空（当 required 为 true 时）
export const requiredArrayField = value => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return 'form.errors.presence';
  }
  return undefined;
};

const SelectFieldWrapper = ({ name, schema, submitting, isCreatable, isMulti, validators, formName }) => {
  const formSelector = useMemo(() => (formName ? formValueSelector(formName) : null), [formName]);

  // Extract array path and index if this select field is directly inside an array
  const match = name ? name.match(/^(.*)\[(\d+)\](?:\.(.*))?$/) : null;
  const parentPath = match ? match[1] : null;
  const itemIndex = match ? parseInt(match[2], 10) : null;
  const relativePath = match ? match[3] : null;

  const parentArrayValue = useSelector(state => {
    try {
      if (!formSelector || !state || !parentPath) return null;
      return formSelector(state, parentPath);
    } catch (error) {
      return null;
    }
  });

  const filteredOptions = useMemo(() => {
    if (!parentArrayValue || !Array.isArray(parentArrayValue)) {
      return schema.options;
    }

    const selectedValues = new Set();
    parentArrayValue.forEach((item, idx) => {
      if (idx === itemIndex) return;

      if (relativePath) {
        const parts = relativePath.split('.');
        let val = item;
        parts.forEach(p => {
          if (val) val = val[p];
        });
        if (val !== undefined && val !== null) {
          selectedValues.add(val);
        }
      } else if (item !== undefined && item !== null) {
        selectedValues.add(item);
      }
    });

    return schema.options.filter(opt => !selectedValues.has(opt.value));
  }, [schema.options, parentArrayValue, itemIndex, relativePath]);

  return (
    <div className="FormSchemaComposer__input-inner FormSchemaComposer__inner">
      {schema.description && <FormattedMessage id={schema.description}>{txt => <p>{txt}</p>}</FormattedMessage>}
      <Field
        name={name}
        options={filteredOptions}
        validate={validators}
        textId={schema.title}
        noErrorMessage={schema.no_error_message}
        component={ReduxSelectField}
        disabled={submitting || schema.disable}
        isCreatable={isCreatable}
        multi={isMulti}
      />
      <style jsx>{staticStyles}</style>
    </div>
  );
};

export const selectComposer = (name, schema, submitting, formName) => {
  // 检查 schema 是否指定了 multi 属性，以支持多选
  const isMulti = schema.multi || false;

  // 检查 schema 是否指定了 creatable 属性，支持自由输入（类似 input 输入框）
  const isCreatable = schema.creatable || false;

  // 针对多选字段，调整验证逻辑
  const validators = [];

  // 当 isCreatable 为 true 时，关闭验证器，允许用户自由输入任意值
  // 当 isCreatable 为 false 时，根据 schema.required 属性决定是否添加验证器
  if (!isCreatable && schema.required !== false) {
    validators.push(requiredSelectField, required());
  }

  return (
    <SelectFieldWrapper
      name={name}
      schema={schema}
      submitting={submitting}
      isCreatable={isCreatable}
      isMulti={isMulti}
      validators={validators}
      formName={formName}
    />
  );
};

// 创建一个 React 组件来处理 regular_account 的条件显示
const RegularAccountObjectField = ({ title, name, schema, formName, submitting, isAllFieldsRequired }) => {
  // 使用 useMemo 稳定 formSelector，避免每次渲染都创建新的 selector
  const formSelector = useMemo(() => formValueSelector(formName), [formName]);
  // 直接获取 type 字段以避免对象引用导致的无限循环
  const programValueRaw = useSelector(state => {
    try {
      if (!formSelector || !state || !formName) return null;

      // 首先尝试直接获取 type 字段
      const typeValue = formSelector(state, 'basic.program.type');
      if (typeValue) {
        return typeValue;
      }

      // 如果 type 字段不存在，尝试从对象中提取
      const value = formSelector(state, 'basic.program');
      if (value && typeof value === 'object' && value.type) {
        return value.type;
      }

      // 如果是字符串，直接返回
      return value;
    } catch (error) {
      return null;
    }
  });

  // 使用 useMemo 缓存 programValue，确保字符串值稳定
  const programValue = useMemo(() => {
    // 确保返回的是字符串或 null，而不是对象
    if (programValueRaw && typeof programValueRaw === 'object' && programValueRaw.type) {
      return programValueRaw.type;
    }
    return programValueRaw;
  }, [programValueRaw]);
  // 只有当 program 为 traded_volume 相关值时才显示
  const isTradedVolume = programValue === 'traded_volume';

  return (
    <ObjectField
      name={name}
      items={schema.properties}
      formName={formName}
      objectTitle={title}
      title={schema.title}
      submitting={submitting}
      hidden={!isTradedVolume}
      isAllFieldsRequired={isAllFieldsRequired}
    />
  );
};

RegularAccountObjectField.propTypes = {
  title: PropTypes.string,
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  isAllFieldsRequired: PropTypes.bool,
};

RegularAccountObjectField.defaultProps = {
  title: '',
  submitting: false,
  isAllFieldsRequired: true,
};

export const objectComposer = (title, name, schema, formName, submitting, hidden, isAllFieldsRequired = true) => {
  // 如果是 regular_account 字段，使用特殊的组件来处理条件显示
  if (name === 'regular_account') {
    return (
      <RegularAccountObjectField
        title={title}
        name={name}
        schema={schema}
        formName={formName}
        submitting={submitting}
        isAllFieldsRequired={isAllFieldsRequired}
      />
    );
  }

  return (
    <ObjectField
      name={name}
      items={schema.properties}
      formName={formName}
      objectTitle={title}
      title={schema.title}
      submitting={submitting}
      hidden={hidden}
      isAllFieldsRequired={isAllFieldsRequired}
    />
  );
};

export const hiddenComposer = (name, title) => (
  <Field name={name} textId={title} type="hidden" hidden component={ReduxTextField} />
);

export const arrLength =
  ({ min, max }) =>
  value => {
    // 如果值为空（null 或 undefined），且有最小长度要求，返回错误
    if (!value) {
      return min !== undefined && min > 0 ? 'form.errors.presence' : undefined;
    }
    // 只有当值存在时才验证长度
    if (min !== undefined && value.length < min) {
      return `less than ${min} items`;
    }
    if (max !== undefined && value.length > max) {
      return `more than ${max} items`;
    }
    return undefined;
  };

export const uniqueValidator = value => {
  if (!value) return;

  const errors = value.map(() => null);

  value.forEach((currentItem, currentIndex) => {
    value.forEach((item, index) => {
      if (typeof currentItem !== 'object') {
        if (currentItem === item) {
          if (currentIndex !== index) {
            errors[currentIndex] = 'uniqueValidationError';
          }
        }
      } else if (JSON.stringify(currentItem) === JSON.stringify(item)) {
        if (currentIndex !== index) {
          errors[currentIndex] = 'uniqueValidationError';
        }
      }
    });
  });

  if (errors.some(elem => elem !== null)) {
    return errors;
  }
};

// 创建一个条件验证器，当字段被隐藏时跳过所有验证
const createConditionalValidator = (validators, isVisible) => value => {
  // 如果字段不可见，跳过所有验证
  if (!isVisible) {
    return undefined;
  }
  // 如果字段可见，执行所有验证器
  const errors = validators.map(validator => validator(value)).filter(error => error);
  return errors.length > 0 ? errors[0] : undefined;
};

// 创建一个 React 组件来处理 pnl_reward 的条件显示
const PnlRewardMultiArrayField = ({ name, schema, formName, submitting }) => {
  const dispatch = useDispatch();
  // 使用 useMemo 稳定 formSelector，避免每次渲染都创建新的 selector
  const formSelector = useMemo(() => formValueSelector(formName), [formName]);
  const programValue = useSelector(state => {
    try {
      if (!formSelector || !state || !formName) return null;
      return formSelector(state, 'basic.program.type');
    } catch (error) {
      return null;
    }
  });
  // 只有当 program 为 pnl 时才显示
  const isPnl = programValue === 'pnl';

  // 获取当前字段值
  const currentValue = useSelector(state => {
    try {
      if (!formSelector || !state || !formName) return null;
      return formSelector(state, name);
    } catch (error) {
      return null;
    }
  });

  // 使用 useRef 跟踪是否已经初始化，避免无限循环
  const hasInitializedRef = useRef(false);
  const prevIsPnlRef = useRef(isPnl);

  // 当 program.type 变化时，管理字段数据
  useEffect(() => {
    // 只在 isPnl 状态变化时执行，而不是在 currentValue 变化时执行
    if (prevIsPnlRef.current !== isPnl) {
      prevIsPnlRef.current = isPnl;
      hasInitializedRef.current = false;
    }

    if (!isPnl) {
      // 当字段隐藏时，清除字段值（只执行一次）
      if (!hasInitializedRef.current) {
        dispatch(change(formName, name, []));
        hasInitializedRef.current = true;
      }
      return;
    }

    // 当字段显示时，如果没有数据，添加一条默认数据（只执行一次）
    if (!hasInitializedRef.current) {
      if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
        dispatch(change(formName, name, [{}]));
      }
      hasInitializedRef.current = true;
    }
  }, [isPnl, formName, name, dispatch]); // 移除 currentValue 从依赖数组

  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 如果 required 为 true，添加必填验证
  if (schema.required === true) {
    validators.push(requiredArrayField);
  }

  // 创建条件验证器，当字段不可见时跳过所有验证
  const conditionalValidator = createConditionalValidator(validators, isPnl);

  return (
    <div style={{ display: isPnl ? 'block' : 'none' }} data-field-name="pnl_reward">
      <FieldArray
        name={name}
        schema={schema}
        validate={conditionalValidator}
        component={MultiArrayField}
        formName={formName}
        arraysName={name}
        submit={submitting}
      />
    </div>
  );
};

PnlRewardMultiArrayField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
};

PnlRewardMultiArrayField.defaultProps = {
  submitting: false,
};

// 创建一个 React 组件来处理 net_deposit_reward 的条件显示
const NetDepositRewardMultiArrayField = ({ name, schema, formName, submitting }) => {
  const dispatch = useDispatch();
  // 使用 useMemo 稳定 formSelector，避免每次渲染都创建新的 selector
  const formSelector = useMemo(() => formValueSelector(formName), [formName]);
  const programValue = useSelector(state => {
    try {
      if (!formSelector || !state || !formName) return null;
      return formSelector(state, 'basic.program.type');
    } catch (error) {
      return null;
    }
  });
  // 只有当 program 为 net_deposit 时才显示
  const isNetDeposit = programValue === 'net_deposit';

  // 获取当前字段值
  const currentValue = useSelector(state => {
    try {
      if (!formSelector || !state || !formName) return null;
      return formSelector(state, name);
    } catch (error) {
      return null;
    }
  });

  // 使用 useRef 跟踪是否已经初始化，避免无限循环
  const hasInitializedRef = useRef(false);
  const prevIsNetDepositRef = useRef(isNetDeposit);

  // 当 program.type 变化时，管理字段数据
  useEffect(() => {
    // 只在 isNetDeposit 状态变化时执行，而不是在 currentValue 变化时执行
    if (prevIsNetDepositRef.current !== isNetDeposit) {
      prevIsNetDepositRef.current = isNetDeposit;
      hasInitializedRef.current = false;
    }

    if (!isNetDeposit) {
      // 当字段隐藏时，清除字段值（只执行一次）
      if (!hasInitializedRef.current) {
        dispatch(change(formName, name, []));
        hasInitializedRef.current = true;
      }
      return;
    }

    // 当字段显示时，如果没有数据，添加一条默认数据（只执行一次）
    if (!hasInitializedRef.current) {
      if (!currentValue || (Array.isArray(currentValue) && currentValue.length === 0)) {
        dispatch(change(formName, name, [{}]));
      }
      hasInitializedRef.current = true;
    }
  }, [isNetDeposit, formName, name, dispatch]); // 移除 currentValue 从依赖数组

  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 如果 required 为 true，添加必填验证
  if (schema.required === true) {
    validators.push(requiredArrayField);
  }

  // 创建条件验证器，当字段不可见时跳过所有验证
  const conditionalValidator = createConditionalValidator(validators, isNetDeposit);

  return (
    <div style={{ display: isNetDeposit ? 'block' : 'none' }} data-field-name="net_deposit_reward">
      <FieldArray
        name={name}
        schema={schema}
        validate={conditionalValidator}
        component={MultiArrayField}
        formName={formName}
        arraysName={name}
        submit={submitting}
      />
    </div>
  );
};

NetDepositRewardMultiArrayField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
};

NetDepositRewardMultiArrayField.defaultProps = {
  submitting: false,
};

export const arrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};

  if (schema.required) {
    validators.push(requiredArrayField);
  }

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={ArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
    />
  );
};

export const grouparrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 从name中提取索引信息
  const index = useMemo(() => {
    if (typeof name === 'string') {
      const match = name.match(/\[(\d+)\]/);
      return match ? parseInt(match[1], 10) : 0;
    }
    return 0;
  }, [name]);

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={GroupArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const mastergrouparrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 从name中提取索引信息
  const index = useMemo(() => {
    if (typeof name === 'string') {
      const match = name.match(/\[(\d+)\]/);
      return match ? parseInt(match[1], 10) : 0;
    }
    return 0;
  }, [name]);

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={MasterGroupArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const generatearrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 从name中提取索引信息
  const index = useMemo(() => {
    if (typeof name === 'string') {
      const match = name.match(/\[(\d+)\]/);
      return match ? parseInt(match[1], 10) : 0;
    }
    return 0;
  }, [name]);

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={GenerateArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const mastergeneratearrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 从name中提取索引信息
  const index = useMemo(() => {
    if (typeof name === 'string') {
      const match = name.match(/\[(\d+)\]/);
      return match ? parseInt(match[1], 10) : 0;
    }
    return 0;
  }, [name]);

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={MasterGenerateArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const symbolgeneratearrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 从name中提取索引信息
  const index = useMemo(() => {
    if (typeof name === 'string') {
      const match = name.match(/\[(\d+)\]/);
      return match ? parseInt(match[1], 10) : 0;
    }
    return 0;
  }, [name]);

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={SymbolGenerateArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const mastersymbolgeneratearrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 从name中提取索引信息
  const index = useMemo(() => {
    if (typeof name === 'string') {
      const match = name.match(/\[(\d+)\]/);
      return match ? parseInt(match[1], 10) : 0;
    }
    return 0;
  }, [name]);

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={MasterSymbolGenerateArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const symbolarrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};
  // 从name属性中提取索引信息
  const index = useMemo(() => {
    // 从name中提取索引部分，例如从 "regular_account.symbol_exceptions[0].reward_amount" 中提取 0
    const match = name.match(/\[(\d+)\]/);
    return match ? parseInt(match[1], 10) : 0;
  }, [name]);

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={SymbolArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const mastersymbolarrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};
  // 从name属性中提取索引信息
  const index = useMemo(() => {
    // 从name中提取索引部分，例如从 "regular_account.symbol_exceptions[0].reward_amount" 中提取 0
    const match = name.match(/\[(\d+)\]/);
    return match ? parseInt(match[1], 10) : 0;
  }, [name]);

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={MasterSymbolArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const accountgrouparrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={AccountGroupArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
    />
  );
};

export const accountsymbolarrayComposer = (name, schema, formName, submitting) => {
  const validators = [];
  const arrLengthParams = {};
  // 从name属性中提取索引信息
  const index = useMemo(() => {
    // 从name中提取索引部分，例如从 "special_account.symbol_exceptions[0].reward_amount" 中提取 0
    const match = name.match(/\[(\d+)\]/);
    return match ? parseInt(match[1], 10) : 0;
  }, [name]);

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={AccountSymbolArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
      index={index}
    />
  );
};

export const dynamicComposer = (name, schema, formName, submitting, isAllFieldsRequired) => (
  <DynamicField
    name={name}
    schema={schema}
    formName={formName}
    submitting={submitting}
    isAllFieldsRequired={isAllFieldsRequired}
  />
);

export const HtmlComposer = (name, schema, submitting) => {
  const templates = useSelector(state => state.form?.commonDynamicForm?.values?.templates || []);
  let isIndependent = false;
  const index = useMemo(() => {
    if (typeof name === 'string') {
      return +name.replace(/[^\d]/g, '');
    }
    return null;
  }, [name]);

  if (index !== null) {
    isIndependent = templates[index]?.is_independent;
  }
  if (isIndependent || schema.title === 'header' || schema.title === 'footer') {
    return (
      <Field
        component={TextAreaRawHtmlField}
        name={name}
        schema={schema}
        disabled={submitting}
        isIndependent={isIndependent}
      />
    );
  }

  return <Field component={TinyMCEField} name={name} schema={schema} disabled={submitting} withScript />;
};

export const dateComposer = (name, schema, submitting) => (
  <Field component={DateField} name={name} disabled={submitting} schema={schema} validate={[required()]} />
);

export const dateRangeComposer = (name, schema, submitting) => {
  const fieldName = name.includes('.') ? name.substring(0, name.lastIndexOf('.')) : name;
  return (
    <Field component={DateRangeField} name={fieldName} disabled={submitting} schema={schema} validate={[required()]} />
  );
};

export const automationRequiredValidator = value => {
  if (value && value.enabled && (!value.ib_codes || !value.ib_codes.length)) {
    return 'Please select at least one IB code.';
  }
  return undefined;
};

export const tagAutomationComposer = (name, schema, submitting) => (
  <Field
    component={TagAutomationField}
    name={name}
    schema={schema}
    disabled={submitting}
    validate={[automationRequiredValidator]}
  />
);

export const multiarrayComposer = (name, schema, formName, submitting) => {
  // 如果是 pnl_reward 字段，使用特殊的组件来处理条件显示
  if (name === 'pnl_reward') {
    return <PnlRewardMultiArrayField name={name} schema={schema} formName={formName} submitting={submitting} />;
  }
  // 如果是 net_deposit_reward 字段，使用特殊的组件来处理条件显示
  if (name === 'net_deposit_reward') {
    return <NetDepositRewardMultiArrayField name={name} schema={schema} formName={formName} submitting={submitting} />;
  }

  const validators = [];
  const arrLengthParams = {};

  if (schema.minItems) {
    arrLengthParams.min = schema.minItems;
  }
  if (schema.maxItems) {
    arrLengthParams.max = schema.maxItems;
  }
  if (Object.keys(arrLengthParams).length) {
    validators.push(arrLength(arrLengthParams));
  }

  if (schema.unique) {
    validators.push(uniqueValidator);
  }

  // 如果 required 为 true，添加必填验证
  if (schema.required === true) {
    validators.push(requiredArrayField);
  }

  return (
    <FieldArray
      name={name}
      schema={schema}
      validate={validators}
      component={MultiArrayField}
      formName={formName}
      arraysName={name}
      submit={submitting}
    />
  );
};

export const FormFieldsComposer = ({
  title,
  name,
  schema,
  formName,
  withoutHeaderDescription,
  submitting,
  isAllFieldsRequired,
}) => {
  switch (schema && schema.widget) {
    case 'text':
    case 'number':
    case 'integer':
    case 'checkbox':
      return inputComposer(name, schema, submitting, withoutHeaderDescription, isAllFieldsRequired, formName);
    case 'select':
      return selectComposer(name, schema, submitting, formName);
    case 'array':
      return arrayComposer(name, schema, formName, submitting);
    case 'multiarray':
      return multiarrayComposer(name, schema, formName, submitting);
    case 'grouparray':
      return grouparrayComposer(name, schema, formName, submitting);
    case 'mastergrouparray':
      return mastergrouparrayComposer(name, schema, formName, submitting);
    case 'symbolarray':
      return symbolarrayComposer(name, schema, formName, submitting);
    case 'accountgrouparray':
      return accountgrouparrayComposer(name, schema, formName, submitting);
    case 'accountsymbolarray':
      return accountsymbolarrayComposer(name, schema, formName, submitting);
    case 'generatearray':
      return generatearrayComposer(name, schema, formName, submitting);
    case 'mastergeneratearray':
      return mastergeneratearrayComposer(name, schema, formName, submitting);
    case 'symbolgeneratearray':
      return symbolgeneratearrayComposer(name, schema, formName, submitting);
    case 'mastersymbolarray':
      return mastersymbolarrayComposer(name, schema, formName, submitting);
    case 'mastersymbolgeneratearray':
      return mastersymbolgeneratearrayComposer(name, schema, formName, submitting);
    case 'object':
      return objectComposer(title, name, schema, formName, submitting, false, isAllFieldsRequired);
    case 'nullform': {
      const hidden = true;
      const nullTitle = '';
      const nullSchema = { ...schema, widget: 'object' };
      return objectComposer(nullTitle, name, nullSchema, formName, submitting, hidden);
    }
    case 'null':
      return hiddenComposer(name, title);
    case 'dynamicform':
      return dynamicComposer(name, schema, formName, submitting, isAllFieldsRequired);
    case 'html':
      return HtmlComposer(name, schema, submitting);
    case 'date':
      return dateComposer(name, schema, submitting);
    case 'date_range':
      return dateRangeComposer(name, schema, submitting);
    case 'tag_automation':
      return tagAutomationComposer(name, schema, submitting);
    case 'note':
      return (
        <div className="FormSchemaComposer__inner" style={{ padding: '0', margin: '0 0 5px 0' }}>
          <p style={{ color: '#000000', fontSize: '14px', margin: '0', lineHeight: '1.5' }}>{schema.text}</p>
        </div>
      );
    default:
      return false;
  }
};

FormFieldsComposer.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  withoutHeaderDescription: PropTypes.bool,
  isAllFieldsRequired: PropTypes.bool,
};

FormFieldsComposer.defaultProps = {
  withoutHeaderDescription: false,
  isAllFieldsRequired: true,
};

export default FormFieldsComposer;
